// The one View Answers modal for portal forms — extracted from the deal-detail
// Client Portal panel so every screen that lists a client's forms (deal panel,
// lead drawers) opens the SAME viewer instead of growing its own.
//
// Two modes, decided by the data:
//   answers  — at least one visible field has a real answer; empties are
//              skipped, exactly what the client filled in.
//   preview  — nothing answered yet; every question shows with a muted "—"
//              so the agent can read the form before the client starts.
import { useMemo } from "react";
import { ExternalLink } from "lucide-react";
import { groupsOf, isFieldVisible } from "../utils/portalForms";

// Is a stored answer effectively empty (nothing to show)? video fields never
// carry an answer, so they always count as empty. Mirrors the on-the-wire
// shapes: multi + repeatable -> array<string>, file -> { name, driveFileId? }
// | null, everything else -> string.
export const isEmptyAnswer = (field, value) => {
  if (field.type === "video") return true;
  if (field.type === "multi" || field.type === "repeatable")
    return !Array.isArray(value) || value.length === 0;
  if (field.type === "file") return !value || !value.name;
  return String(value ?? "").trim() === "";
};

// createdAt/expiresAt may be millis or ISO strings — Date() handles both.
const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function PortalFormAnswersModal({ form, entry, onClose }) {
  // Does the open form have any real answers? If not the modal is a read-only
  // PREVIEW of the questions — so the agent can see every form before the
  // client has filled anything in. When answers exist it keeps the original
  // "View answers" behavior (empties skipped).
  const preview =
    !!form &&
    !form.fields.some(
      (f) =>
        isFieldVisible(f, entry?.answers || {}) &&
        !isEmptyAnswer(f, entry?.answers?.[f.key]),
    );

  // Rows for the modal, grouped. Answers mode skips empties; preview mode
  // shows every field, labelled, with a muted "—" placeholder.
  const answerGroups = useMemo(() => {
    if (!form) return [];
    const answers = entry?.answers || {};
    return groupsOf(form)
      .map((group) => {
        // Skip fields hidden by showIf (against this form's saved answers) so
        // the agent view matches exactly what the client saw.
        const groupFields = form.fields.filter(
          (f) => (f.group || "") === group && isFieldVisible(f, answers),
        );
        const rows = preview
          ? groupFields
          : groupFields.filter((f) => !isEmptyAnswer(f, answers[f.key]));
        return { group, rows };
      })
      .filter((g) => g.rows.length > 0);
  }, [form, entry, preview]);

  if (!form) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-slate-900 text-sm truncate">
            {form.title}
            {entry?.submittedAt
              ? ` — submitted ${fmtDate(entry.submittedAt)}`
              : preview
                ? " — preview"
                : " — in progress"}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            ✕
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          {answerGroups.length === 0 ? (
            <p className="text-sm text-slate-500">No answers were filled in.</p>
          ) : (
            answerGroups.map(({ group, rows }) => (
              <div key={group} className="mb-5 last:mb-0">
                {group && (
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    {group}
                  </h4>
                )}
                <div className="divide-y divide-slate-100">
                  {rows.map((f) => {
                    const value = entry?.answers?.[f.key];
                    return (
                      <div
                        key={f.key}
                        className="flex items-baseline justify-between gap-4 py-1.5"
                      >
                        <span className="text-sm text-gray-500">{f.label}</span>
                        <span className="text-sm font-semibold text-right break-words">
                          {f.type === "video" ? (
                            <span className="font-normal text-slate-400">
                              Video clip
                            </span>
                          ) : isEmptyAnswer(f, value) ? (
                            <span className="font-normal text-slate-400">—</span>
                          ) : f.type === "multi" || f.type === "repeatable" ? (
                            (Array.isArray(value) ? value : []).join(", ")
                          ) : f.type === "file" ? (
                            value?.driveFileId ? (
                              <a
                                href={`https://drive.google.com/file/d/${value.driveFileId}/view`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-linkColor hover:underline"
                              >
                                <ExternalLink size={12} /> {value.name}
                              </a>
                            ) : (
                              value?.name || ""
                            )
                          ) : Array.isArray(value) ? (
                            value.join(", ")
                          ) : (
                            value
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
